import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { PusherEventName } from 'anchor-scout-common/types';
import { Button, Callout, Typography, useDismissBottomSheet, useIsMounted, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { accountingIconMap } from '~/integrations/Accounting/AccountingIcon/accountingIconMap';
import { PayoutIntegrationModel } from '~/models/PayoutIntegrationModel';
import { useBusinessChannelEventBinder } from '~/pusher/useBusinessChannelEventBinder';
import { Accounting } from '~/types/accounting';
import { accountingSyncErrorTextsPath } from '~/utils/getAccountingInvoiceErrorMessage.utils';
import styles from './PayoutAccountingSection.module.scss';
import { getAccountingInfoStatus, getCalloutIntent, getInfo, payoutAccountingSectionTextsPath } from './PayoutAccountingSection.utils';
export const GetPayoutAccountingSectionTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            PayoutAccountingSectionTID: {
                RetrySync: 'retry-sync-payout',
                Sync: 'sync-payout',
                Info: (testid)=>testid ? "".concat(testid, "-info") : ''
            }
        }
    });
// WHY not in GetPayoutAccountingSectionTID: toasts are global overlay elements; a parentTestId-derived id would vary per payout row and break PayoutsDashboard's toast assertions
export const PayoutSyncToastIds = {
    Success: 'sync-success-toast',
    Failure: 'sync-failure-toast'
};
const pusherEventNameByAccounting = {
    [Accounting.QBO]: PusherEventName.PayoutQboIntegrationStatusUpdated,
    [Accounting.Xero]: PusherEventName.PayoutXeroIntegrationStatusUpdated
};
export function PayoutAccountingSection(props) {
    const { payoutId, payoutIntegration, testId, accounting } = props;
    const isMounted = useIsMounted();
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const hideBottomSheet = useDismissBottomSheet();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: payoutsTexts } = useLocaleKeys('pages.payoutsDashboard');
    const { t: accountingSectionTexts } = useLocaleKeys(payoutAccountingSectionTextsPath);
    const { t: accountingSyncErrorTexts } = useLocaleKeys(accountingSyncErrorTextsPath);
    const { api } = useStoreActions();
    const { PayoutAccountingSectionTID } = useMemo(()=>GetPayoutAccountingSectionTID(testId), [
        testId
    ]);
    const [isSyncing, setIsSyncing] = useState(false);
    const status = getAccountingInfoStatus(payoutIntegration);
    const info = getInfo({
        payoutIntegration,
        accounting,
        accountingSectionTexts,
        accountingSyncErrorTexts
    });
    const { startListening } = useBusinessChannelEventBinder(pusherEventNameByAccounting[accounting], 'scout');
    async function syncPayout() {
        setIsSyncing(true);
        startListening((event)=>{
            if (event.payoutId !== payoutId) return;
            const match = event.integrations.find((i)=>i.type === accounting);
            if (!match) return;
            const updatedIntegration = PayoutIntegrationModel.fromDTO(match.value);
            if (updatedIntegration.isSyncFailed()) {
                setIsSyncing(false);
                hideBottomSheet();
                openSyncToast('failure');
            } else if (updatedIntegration.isSynced()) {
                setIsSyncing(false);
                hideBottomSheet();
                openSyncToast('success');
            }
        });
        const { success } = await handleRequest(api.accounting.syncPayout({
            accounting,
            payoutId
        }));
        if (!isMounted()) return;
        if (!success) {
            setIsSyncing(false);
            openSyncToast('failure');
        }
    }
    function openSyncToast(type) {
        if (type === 'success') {
            showSuccessToast(commonTexts.toasts.payoutSuccessfullySynced(), PayoutSyncToastIds.Success);
        } else {
            showErrorToast(commonTexts.toasts.failedToSyncPayout(), PayoutSyncToastIds.Failure);
        }
    }
    if (status === 'success') {
        return null;
    }
    return /*#__PURE__*/ _jsx(Callout, {
        intent: getCalloutIntent(status),
        icon: accountingIconMap[accounting][status],
        withBorder: true,
        hideIcon: false,
        testId: testId,
        "data-status": status,
        classes: {
            wrapper: styles.callout
        },
        withAction: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                payoutIntegration.isSyncFailed() && /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'retry_payout_accounting_sync'
                    },
                    className: styles.retrySyncBtn,
                    skin: "primaryOutline",
                    size: "small",
                    testId: PayoutAccountingSectionTID.RetrySync(),
                    loading: isSyncing,
                    onClick: ()=>syncPayout(),
                    children: payoutsTexts.retrySync()
                }),
                payoutIntegration.isPendingSync() && /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'sync_payout_to_accounting'
                    },
                    testId: PayoutAccountingSectionTID.Sync(),
                    onClick: ()=>syncPayout(),
                    skin: "primaryOutline",
                    size: "small",
                    loading: isSyncing,
                    children: payoutsTexts.sync()
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx(Typography, {
            testId: PayoutAccountingSectionTID.Info(testId),
            children: info
        })
    });
}
